import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Switch,
  FormControlLabel,
  Divider,
  Button,
  CircularProgress,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../store/Index";
import CustomTextField from "../../components/common/Textfield";
import apiService from "../../service/Api";
import { toast } from "react-toastify";
import PhoneNumberField from "../../components/common/PhoneNumberField";

interface Company {
  name: string;
  company_type: string;
  is_active: boolean;
  description?: string;

  phone_number?: string;
  primary_email?: string;
  secondary_email?: string;
  website?: string;

  street_address?: string;
  billing_address?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;

  latitude?: string;
  longitude?: string;
  time_zone?: string;

  license_number?: string;
  specialties?: string;

  company_logo?: string;
}

const EditCompanyForm: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const reduxTheme = useSelector((state: RootState) => state.theme);

  const [loading, setLoading] = useState(true);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    company_name: "",
    company_type: "",
    is_active: true,
    description: "",

    phone_number: "",
    primary_email: "",
    secondary_email: "",
    website: "",

    street_address_1: "",
    street_address_2: "",
    city: "",
    state: "",
    postal_code: "",
    country: "",

    latitude: "",
    longitude: "",
    time_zone: "",

    license_number: "",
    service_specialties: "",
    logo: null as File | null,
  });

  const handleChange = (
    field: keyof typeof formData,
    value: string | boolean | File | null,
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const textFieldStyles = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      "& fieldset": {
        borderColor: reduxTheme.primary_colors?.[300] || "#ddd",
      },
      "&:hover fieldset": {
        borderColor: reduxTheme.primary_colors?.[300] || "#bbb",
      },
      "&.Mui-focused fieldset": {
        borderColor: reduxTheme.primary_colors?.[400] || "#b8865b",
      },
    },
    "& .MuiInputLabel-root": {
      color: reduxTheme.primary_colors?.[300] || "#666",
    },
    "& .MuiInputBase-input": {
      color: reduxTheme.neutral_colors?.[100] || "#333",
    },
  };
  // ✅ Fetch company details
  useEffect(() => {
    const fetchCompany = async () => {
      try {
        const response = await apiService.get<Company>(
          `api/companies/company/${id}/`,
        );

        const data = response.data;

        setFormData({
          company_name: data.name || "",
          company_type: data.company_type || "",
          is_active: data.is_active,
          description: data.description || "",

          phone_number: data.phone_number || "",
          primary_email: data.primary_email || "",
          secondary_email: data.secondary_email || "",
          website: data.website || "",

          street_address_1: data.street_address || "",
          street_address_2: data.billing_address || "",
          city: data.city || "",
          state: data.state || "",
          postal_code: data.postal_code || "",
          country: data.country || "",

          latitude: data.latitude || "",
          longitude: data.longitude || "",
          time_zone: data.time_zone || "",

          license_number: data.license_number || "",
          service_specialties: data.specialties || "",
          logo: null,
        });

        // ✅ Set preview if logo exists
        if (data.company_logo) {
          setLogoPreview(data.company_logo);
        }

        setLoading(false);
      } catch (error) {
        console.error("Failed to fetch company", error);
        setLoading(false);
      }
    };

    if (id) fetchCompany();
  }, [id]);

  // ✅ PATCH request
  const handleSubmit = async () => {
    try {
      const payload = new FormData();

      payload.append("name", formData.company_name);
      payload.append("company_type", formData.company_type);
      payload.append("is_active", formData.is_active.toString());
      payload.append("description", formData.description);

      payload.append("phone_number", formData.phone_number);
      payload.append("primary_email", formData.primary_email);
      payload.append("secondary_email", formData.secondary_email);
      payload.append("website", formData.website);

      payload.append("street_address", formData.street_address_1);
      payload.append("billing_address", formData.street_address_2);
      payload.append("city", formData.city);
      payload.append("state", formData.state);
      payload.append("postal_code", formData.postal_code);
      payload.append("country", formData.country);

      payload.append("license_number", formData.license_number);
      payload.append("specialties", formData.service_specialties);

      if (formData.logo) {
        payload.append("company_logo", formData.logo);
      }

      await apiService.patch(`api/companies/company/${id}/`, payload);

      toast("Company updated successfully!");
      navigate(`/companies/${id}`);
    } catch (error: unknown) {
      if (error instanceof Error) {
        console.error("Update failed:", error.message);
        toast(`Failed to update company: ${error.message}`);
      } else {
        console.error("Update failed:", error);
        toast("Failed to update company.");
      }
    }
  };

  if (loading) {
    return (
      <Box p={5}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: 4,
        maxWidth: 1200,
        borderRadius: 3,
        background: reduxTheme.neutral_colors?.[900] || "#fff",
      }}
    >
      <Typography variant="h4" fontWeight={600} gutterBottom>
        Edit Company
      </Typography>

      {/* Basic Info */}
      <Typography
        variant="h6"
        sx={{ mt: 3, color: reduxTheme.primary_colors?.[400] || "#614438" }}
      >
        Company Details
      </Typography>

      <Grid container spacing={3} mt={1}>
        <Grid size={{ xs: 12, md: 6 }}>
          <CustomTextField
            label="Company Name"
            fullWidth
            value={formData.company_name}
            onChange={(e) => handleChange("company_name", e.target.value)}
            sx={textFieldStyles}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <CustomTextField
            label="Company Type"
            fullWidth
            value={formData.company_type}
            onChange={(e) => handleChange("company_type", e.target.value)}
            sx={textFieldStyles}
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.is_active}
                onChange={(e) => handleChange("is_active", e.target.checked)}
              />
            }
            label="Is Active"
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <CustomTextField
            label="Description"
            fullWidth
            multiline
            rows={3}
            value={formData.description}
            onChange={(e) => handleChange("description", e.target.value)}
            sx={textFieldStyles}
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 4 }} />

      {/* Contact Information */}
      <Typography
        variant="h6"
        sx={{ color: reduxTheme.primary_colors?.[400] || "#614438" }}
      >
        Contact Information
      </Typography>

      <Grid container spacing={3} mt={1}>
        <Grid size={{ xs: 12, md: 6 }}>
          <PhoneNumberField
            defaultCountry="ZW"
            value={formData.phone_number}
            onChange={(phone: string) => handleChange("phone_number", phone)}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <CustomTextField
            label="Primary Email Address"
            fullWidth
            value={formData.primary_email}
            onChange={(e) => handleChange("primary_email", e.target.value)}
            sx={textFieldStyles}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <CustomTextField
            label="Secondary Email Address"
            fullWidth
            value={formData.secondary_email}
            onChange={(e) => handleChange("secondary_email", e.target.value)}
            sx={textFieldStyles}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <CustomTextField
            label="Website"
            fullWidth
            value={formData.website}
            onChange={(e) => handleChange("website", e.target.value)}
            sx={textFieldStyles}
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 4 }} />

      {/* Address */}
      <Typography
        variant="h6"
        sx={{ color: reduxTheme.primary_colors?.[400] || "#614438" }}
      >
        Address & Location
      </Typography>

      <Grid container spacing={3} mt={1}>
        <Grid size={{ xs: 12 }}>
          <CustomTextField
            label="Street Address 1"
            fullWidth
            value={formData.street_address_1}
            onChange={(e) => handleChange("street_address_1", e.target.value)}
            sx={textFieldStyles}
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <CustomTextField
            label="Street Address 2"
            fullWidth
            value={formData.street_address_2}
            onChange={(e) => handleChange("street_address_2", e.target.value)}
            sx={textFieldStyles}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <CustomTextField
            label="City"
            fullWidth
            value={formData.city}
            onChange={(e) => handleChange("city", e.target.value)}
            sx={textFieldStyles}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <CustomTextField
            label="State/Province"
            fullWidth
            value={formData.state}
            onChange={(e) => handleChange("state", e.target.value)}
            sx={textFieldStyles}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <CustomTextField
            label="Postal Code"
            fullWidth
            value={formData.postal_code}
            onChange={(e) => handleChange("postal_code", e.target.value)}
            sx={textFieldStyles}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <CustomTextField
            label="Country"
            fullWidth
            value={formData.country}
            onChange={(e) => handleChange("country", e.target.value)}
            sx={textFieldStyles}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 2 }}>
          <CustomTextField
            label="Latitude"
            fullWidth
            value="-17.7798"
            disabled
            sx={textFieldStyles}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 2 }}>
          <CustomTextField
            label="Longitude"
            fullWidth
            value="31.0573"
            disabled
            sx={textFieldStyles}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <CustomTextField
            label="Time Zone"
            fullWidth
            value="Africa/Harare"
            disabled
            sx={textFieldStyles}
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 4 }} />

      {/* Business Details */}
      <Typography
        variant="h6"
        sx={{ color: reduxTheme.primary_colors?.[400] || "#614438" }}
      >
        Funeral / Business Details
      </Typography>

      <Grid container spacing={3} mt={1}>
        <Grid size={{ xs: 12, md: 6 }}>
          <CustomTextField
            label="License Number"
            fullWidth
            value={formData.license_number}
            onChange={(e) => handleChange("license_number", e.target.value)}
            sx={textFieldStyles}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <CustomTextField
            label="Service Specialties"
            fullWidth
            placeholder="e.g., Cremation, Memorial Services"
            value={formData.service_specialties}
            onChange={(e) =>
              handleChange("service_specialties", e.target.value)
            }
            sx={textFieldStyles}
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 4 }} />

      {/* Branding */}
      <Typography
        variant="h6"
        sx={{ color: reduxTheme.primary_colors?.[400] || "#614438" }}
      >
        Branding
      </Typography>

      <Box mt={2}>
        <Button variant="outlined" component="label">
          Upload Logo
          <input
            hidden
            type="file"
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files ? e.target.files[0] : null;
              handleChange("logo", file);
              if (file) {
                setLogoPreview(URL.createObjectURL(file)); // new preview
              }
            }}
          />
        </Button>
        {logoPreview && (
          <Box mt={2}>
            <Typography variant="subtitle2">Logo Preview:</Typography>
            <img
              src={logoPreview}
              alt="Company Logo"
              style={{
                width: 120,
                height: 120,
                objectFit: "contain",
                borderRadius: 8,
              }}
            />
          </Box>
        )}
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Submit */}
      <Button variant="contained" size="large" onClick={handleSubmit}>
        Save Company
      </Button>
    </Box>
  );
};

export default EditCompanyForm;
